import { useState, useMemo, useEffect, Fragment } from "react";
import { useBranch } from "@/context/BranchContext";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Eye,
  Pencil,
  QrCode,
  Plus,
  Download,
  Printer,
  MoreHorizontal,
  Loader2,
  Trash2,
  MoveRight,
  History,
  Users,
  Upload,
  ChevronRight,
  Layers,
  CornerDownRight,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useAssets, useDeleteAsset, useSubAssets, type AssetListFilters, type AssetListRow } from "@/features/assets/api";
import { AssetFilters } from "@/features/assets/AssetFilters";
import { StatusBadge, AllocationStatusBadge } from "@/features/assets/badges";
import { QrModal, useQrModalState, printBulkLabels } from "@/features/assets/QrModal";
import { AllocateAssetDialog } from "@/features/assets/AllocateAssetDialog";
import { AssetKpis, type AssetKpiKey } from "@/features/assets/kpis";
import { shapeAssetRows } from "@/features/assets/excel";
import { fetchAllPages } from "@/features/assets/exportAll";
import { ReportExportDialog } from "@/features/reports/export/ReportExportDialog";
import type { ExportRow } from "@/features/reports/export";
import { apiList } from "@/integrations/api/client";
import { useAssetListIndicators } from "@/features/assets/reliability";
import { AssetIndicatorBadges } from "@/features/assets/AssetIndicatorBadges";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canCreateAsset, canEditAsset, canDeleteAsset, canAllocateAsset, isAssetOnboardingComplete } from "@/lib/roles";
import type { AppRole } from "@/lib/roles";
import { assetActions } from "@/features/assets/allocationActions";
import { isDisposed } from "@/features/assets/lifecycle";
import { getDisplayAllocationStatus } from "@/features/assets/format";
import { cn } from "@/lib/utils";

type AssetSearchSchema = {
  search?: string;
  hierarchy?: "parent" | "sub_asset" | "standalone";
  status?: string;
  page?: number;
};

export const Route = createFileRoute("/_authenticated/assets/")({
  validateSearch: (s: Record<string, unknown>): AssetSearchSchema => ({
    search: typeof s.search === "string" ? s.search : undefined,
    hierarchy: ["parent", "sub_asset", "standalone"].includes(s.hierarchy as string)
      ? (s.hierarchy as "parent" | "sub_asset" | "standalone")
      : undefined,
    status: typeof s.status === "string" ? s.status : undefined,
    page: s.page ? Number(s.page) : undefined,
  }),
  component: AssetsPage,
});

function AssetsPage() {
  const navigate = Route.useNavigate();

  const { data: me } = useCurrentUser();
  const roles = (me?.roles ?? []) as AppRole[];
  const canCreate = canCreateAsset(roles);
  const canEdit = canEditAsset(roles);
  const canDelete = canDeleteAsset(roles);
  const canExport = roles.includes("super_admin") || (me?.permissions?.includes("export_assets") ?? false);

  const { currentBranchId, canSwitch, lockedBranchId } = useBranch();
  const routeSearch = Route.useSearch();

  const [filters, setFilters] = useState<AssetListFilters>({
    branchId: lockedBranchId ?? currentBranchId ?? null,
    includeInStore: false,
    search: routeSearch.search ?? "",
    hierarchyType: routeSearch.hierarchy ?? null,
    status: routeSearch.status ?? null,
    page: routeSearch.page ?? 1,
    pageSize: 25,
  });

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleExpand = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Keep the page-level branch filter in sync with the top-bar branch context.
  useEffect(() => {
    const next = lockedBranchId ?? currentBranchId ?? null;
    setFilters((f) => (f.branchId === next ? f : { ...f, branchId: next, page: 1 }));
  }, [currentBranchId, lockedBranchId]);

  // Sync search and filter state to route URL
  useEffect(() => {
    navigate({
      search: {
        search: filters.search || undefined,
        hierarchy: filters.hierarchyType || undefined,
        status: filters.status || undefined,
        page: filters.page && filters.page > 1 ? filters.page : undefined,
      },
      replace: true,
    });
  }, [filters.search, filters.hierarchyType, filters.status, filters.page, navigate]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [toDelete, setToDelete] = useState<AssetListRow | null>(null);
  const [toAllocate, setToAllocate] = useState<AssetListRow | null>(null);
  const [kpiFilter, setKpiFilter] = useState<AssetKpiKey | null>(null);
  const effectiveFilters = useMemo<AssetListFilters>(() => {
    if (!kpiFilter || kpiFilter === "total") return filters;
    const f: AssetListFilters = { ...filters, page: 1 };
    if (kpiFilter === "allocated") f.allocationStatus = "Allocated";
    else if (kpiFilter === "unallocated") f.allocationStatus = "Unallocated";
    else if (kpiFilter === "under_repair") f.status = "Under Repair";
    return f;
  }, [filters, kpiFilter]);
  const { data, isLoading } = useAssets(effectiveFilters);
  const deleteMut = useDeleteAsset();
  const queryClient = useQueryClient();
  const qrModal = useQrModalState();
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / (filters.pageSize ?? 25)));
  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const { data: indicators } = useAssetListIndicators(rowIds);

  const selectableRows = useMemo(() => rows.filter((r) => !isDisposed(r)), [rows]);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.id));
  const someSelected = selected.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(selectableRows.map((r) => r.id)));
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);

  const renderLocation = (a: AssetListRow) => a.location?.name ?? "—";

  const [exportOpen, setExportOpen] = useState(false);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (type: "Current" | "All" | "Major" | "Minor") => {
    if (type === "Current") {
      setExportRows(shapeAssetRows(rows));
      setExportOpen(true);
      return;
    }
    setIsExporting(true);
    try {
      // every asset of the selected branch, including those kept in store, fetched page by page (the API caps page size)
      const apiParams: Record<string, string | number | undefined> = {
        limit: 1000,
        branch_id: filters.branchId ?? undefined,
      };
      if (type !== "All") {
        apiParams.asset_nature = type;
      }

      const { rows: all, total, complete } = await fetchAllPages<AssetListRow>((page) =>
        apiList<AssetListRow>("/api/v1/assets/", { ...apiParams, page }),
      );
      if (!complete) toast.warning(`Assets changed while exporting (${all.length} of ${total} rows). Export again for a consistent file.`);
      setExportRows(shapeAssetRows(all));
      setExportOpen(true);
    } catch (e: any) {
      toast.error(e.message || "Failed to fetch assets for export");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Asset Registry</h1> <ReadOnlyPill module="assets" />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: "/assets/qr-labels",
                search: someSelected
                  ? { selected: Array.from(selected).join(",") }
                  : {},
              })
            }
          >
            <Printer className="mr-1 h-4 w-4" />
            {someSelected ? `Print ${selected.size} QR Labels` : "Print QR Labels"}
          </Button>
          {someSelected && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <QrCode className="mr-1 h-4 w-4" />
                  Quick print {selected.size}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => printBulkLabels(selectedRows, "50x25")}>
                  50×25 mm labels
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => printBulkLabels(selectedRows, "75x50")}>
                  75×50 mm labels
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canExport && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={isExporting || (!rows.length && !isExporting)}>
                    {isExporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => handleExport("Current")}>Export Current View</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport("All")}>Export All Assets</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport("Major")}>Export Major Assets</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport("Minor")}>Export Minor Assets</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ReportExportDialog
                open={exportOpen}
                onOpenChange={setExportOpen}
                reportKey="R-01"
                title="Asset Register"
                filename="Asset_Register"
                rows={exportRows}
                requiredColumns={["Asset Code"]}
              />
            </>
          )}
          {canCreate && (
            <Button variant="outline" onClick={() => navigate({ to: "/assets/import" })}>
              <Upload className="mr-1 h-4 w-4" /> Import CSV
            </Button>
          )}

          {canCreate && (
            <Button onClick={() => navigate({ to: "/assets/new" })}>
              <Plus className="mr-1 h-4 w-4" /> Add New Asset
            </Button>
          )}

        </div>
      </div>

      <AssetKpis
        branchId={filters.branchId ?? null}
        activeKey={kpiFilter}
        onSelect={(k) => setKpiFilter(k)}
      />

      <Card className="p-4">
        <AssetFilters filters={filters} onChange={setFilters} />
        <div className="mt-4 flex justify-end border-t pt-3 text-xs text-muted-foreground">
          Showing {rows.length} of {total} assets
        </div>
      </Card>


      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
              </TableHead>
              <TableHead className="whitespace-nowrap">Asset Code</TableHead>
              <TableHead>Asset Name</TableHead>
              <TableHead className="whitespace-nowrap">Hierarchy</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Asset Type</TableHead>
              <TableHead>Nature</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Asset Status</TableHead>
              <TableHead>Allocation Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={13} className="py-12 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} className="py-12 text-center text-muted-foreground">
                  No assets found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((a) => {
                const disposed = isDisposed(a);
                const building = a.asset_class === "Infrastructure" || a.asset_type === "Building";
                const allowedTypes = ["Equipment","Infrastructure","Vehicle","IT","Furniture","Other"];
                const assetType = a.asset_class && allowedTypes.includes(a.asset_class) ? a.asset_class : "Unclassified";
                const assetName = a.category?.name ?? a.name;
                return (
                  <Fragment key={a.id}>
                    <TableRow
                      className={cn("cursor-pointer", disposed && "opacity-60 bg-muted/40 text-muted-foreground", expandedRows.has(a.id) && "bg-muted/10")}
                      onClick={() => navigate({ to: "/assets/$assetId", params: { assetId: a.id } })}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {(a.sub_assets_count ?? 0) > 0 ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground shrink-0"
                              onClick={() => toggleExpand(a.id)}
                              aria-label={expandedRows.has(a.id) ? "Collapse sub assets" : "Expand sub assets"}
                            >
                              <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", expandedRows.has(a.id) && "rotate-90 text-emerald-600")} />
                            </Button>
                          ) : (
                            <div className="w-5 shrink-0" />
                          )}
                          <Checkbox
                            checked={disposed ? false : selected.has(a.id)}
                            onCheckedChange={() => !disposed && toggleOne(a.id)}
                            disabled={disposed}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        {a.asset_code ? (
                          <span className="font-semibold">{a.asset_code}</span>
                        ) : (
                          <Badge variant="outline" className="text-[10px] text-amber-700 bg-amber-50 border-amber-300 font-medium">
                            Not Assigned
                          </Badge>
                        )}
                        {a.asset_class === "Vehicle" && a.vehicle_registration_number && (
                          <div className="text-[10px] text-muted-foreground">{a.vehicle_registration_number}</div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-1.5">
                          {a.parent_asset && (
                            <span
                              className="text-indigo-600 dark:text-indigo-400 font-bold text-xs"
                              title={`Sub-Asset of ${a.parent_asset.name}`}
                            >
                              ↳
                            </span>
                          )}
                          <span>{assetName}</span>
                          <AssetIndicatorBadges assetId={a.id} indicator={indicators?.get(a.id)} />
                        </div>
                        {a.serial_number && (
                          <div className="text-[11px] text-muted-foreground font-mono">
                            S/N: {a.serial_number}
                          </div>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()} className="whitespace-nowrap">
                        {(a.sub_assets_count ?? 0) > 0 ? (
                          <div className="flex flex-col gap-1 items-start">
                            <Badge
                              variant="outline"
                              className="text-[10px] font-semibold tracking-wide bg-emerald-50 text-emerald-700 border-emerald-300 flex items-center gap-1 px-1.5 py-0.5"
                            >
                              <Layers className="h-3 w-3 text-emerald-600" />
                              Parent Asset
                            </Badge>
                            <button
                              type="button"
                              className="text-[11px] text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1 font-medium transition-colors cursor-pointer"
                              onClick={() => toggleExpand(a.id)}
                              title={expandedRows.has(a.id) ? "Hide linked sub assets" : "Show linked sub assets"}
                            >
                              <span>{expandedRows.has(a.id) ? "Hide" : "Show"} {a.sub_assets_count} {a.sub_assets_count === 1 ? "Sub Asset" : "Sub Assets"}</span>
                              <ChevronRight className={cn("h-3 w-3 transition-transform text-emerald-600", expandedRows.has(a.id) && "rotate-90")} />
                            </button>
                          </div>
                        ) : a.parent_asset ? (
                          <div className="flex flex-col gap-0.5 items-start">
                            <Badge
                              variant="outline"
                              className="text-[10px] font-semibold tracking-wide bg-indigo-50 text-indigo-700 border-indigo-200 flex items-center gap-1 px-1.5 py-0.5"
                            >
                              <CornerDownRight className="h-3 w-3 text-indigo-600" />
                              Sub Asset
                            </Badge>
                            <div className="flex items-center gap-1 text-[11px] text-muted-foreground max-w-[190px]">
                              <span className="text-[10px] uppercase font-semibold text-muted-foreground/80 shrink-0">Parent:</span>
                              <button
                                type="button"
                                className="text-[11px] text-primary hover:underline text-left truncate font-medium inline-flex items-center gap-0.5 cursor-pointer"
                                onClick={() => navigate({ to: "/assets/$assetId", params: { assetId: a.parent_asset!.id } })}
                                title={`Parent Asset: ${a.parent_asset.name} (${a.parent_asset.asset_code || 'Not Assigned'})`}
                              >
                                <span className="truncate">{a.parent_asset.asset_code ? `${a.parent_asset.asset_code} · ${a.parent_asset.name}` : a.parent_asset.name}</span>
                                <ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-60" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">Standalone</span>
                        )}
                      </TableCell>
                    <TableCell className="max-w-[240px] text-sm text-muted-foreground">
                      {(a.item_description || a.name) ? (
                        <span className="line-clamp-2" title={a.item_description || a.name}>{a.item_description || a.name}</span>
                      ) : "—"}
                    </TableCell>
                    <TableCell><Badge variant="secondary">{assetType}</Badge></TableCell>
                    <TableCell>
                      {a.asset_nature ? (
                        <Badge variant="outline" className={a.asset_nature === "Major" ? "border-amber-500 text-amber-600" : ""}>{a.asset_nature}</Badge>
                      ) : "—"}
                    </TableCell>
                    <TableCell>{a.branch?.name ?? "—"}</TableCell>
                    <TableCell>{a.department?.name ?? "—"}</TableCell>
                    <TableCell>{renderLocation(a)}</TableCell>
                    <TableCell>
                      <StatusBadge value={a.status} />
                    </TableCell>
                    <TableCell>
                      {building
                        ? <AllocationStatusBadge value="Not Applicable" />
                        : <AllocationStatusBadge value={getDisplayAllocationStatus(a)} />}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {disposed ? (
                            <>
                              <DropdownMenuItem
                                onClick={() => navigate({ to: "/assets/$assetId", params: { assetId: a.id } })}
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({ to: "/assets/$assetId", params: { assetId: a.id }, hash: "disposal" })
                                }
                              >
                                <History className="mr-2 h-4 w-4" />
                                View Disposal Record
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({ to: "/assets/$assetId", params: { assetId: a.id }, hash: "history" })
                                }
                              >
                                <History className="mr-2 h-4 w-4" />
                                View History
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => qrModal.show(a)}>
                                <QrCode className="mr-2 h-4 w-4" />
                                QR Label
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <>
                              {(() => {
                                const actions = assetActions(a);
                                const canAllocate = canAllocateAsset(roles, a);
                                const onboardingDone = isAssetOnboardingComplete(a);
                                const allocatorBase = roles.some((r) =>
                                  ["super_admin", "biomed_head", "facility_manager", "purchase_stores"].includes(r),
                                );
                                const showAllocateDisabled =
                                  allocatorBase &&
                                  !onboardingDone &&
                                  (actions.includes("allocate") || actions.includes("allocate_remaining"));
                                return (
                                  <>
                                    {canAllocate && onboardingDone && actions.includes("allocate") && (
                                      <DropdownMenuItem onClick={() => setToAllocate(a)}>
                                        <MoveRight className="mr-2 h-4 w-4" />
                                        Allocate
                                      </DropdownMenuItem>
                                    )}
                                    {canAllocate && onboardingDone && actions.includes("allocate_remaining") && (
                                      <DropdownMenuItem onClick={() => setToAllocate(a)}>
                                        <MoveRight className="mr-2 h-4 w-4" />
                                        Allocate Remaining
                                      </DropdownMenuItem>
                                    )}
                                    {showAllocateDisabled && (
                                      <DropdownMenuItem
                                        disabled
                                        title="Asset onboarding is not complete. Complete technical verification, finance capitalisation, and installation before allocation."
                                      >
                                        <MoveRight className="mr-2 h-4 w-4" />
                                        Allocate
                                      </DropdownMenuItem>
                                    )}
                                    {actions.includes("view_allocations") && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          navigate({
                                            to: "/assets/$assetId",
                                            params: { assetId: a.id },
                                            hash: "history",
                                          })
                                        }
                                      >
                                        <Users className="mr-2 h-4 w-4" />
                                        View Allocations
                                      </DropdownMenuItem>
                                    )}
                                    {canAllocate && actions.includes("request_transfer") && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          navigate({
                                            to: "/assets/$assetId/transfer/new",
                                            params: { assetId: a.id },
                                          })
                                        }
                                      >
                                        <MoveRight className="mr-2 h-4 w-4" />
                                        Request Transfer
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                );
                              })()}
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate({ to: "/assets/$assetId", params: { assetId: a.id }, hash: "history" })
                                }
                              >
                                <History className="mr-2 h-4 w-4" />
                                Allocation History
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => navigate({ to: "/assets/$assetId", params: { assetId: a.id } })}
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                View
                              </DropdownMenuItem>
                              {canEdit && (
                                <DropdownMenuItem
                                  onClick={() =>
                                    navigate({ to: "/assets/$assetId/edit", params: { assetId: a.id } })
                                  }
                                >
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => qrModal.show(a)}>
                                <QrCode className="mr-2 h-4 w-4" />
                                QR Label
                              </DropdownMenuItem>
                              {canDelete && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => setToDelete(a)}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  {expandedRows.has(a.id) && (
                    <TableRow key={`${a.id}-sub-assets-expanded`} className="bg-muted/15 hover:bg-muted/20 border-b">
                      <TableCell colSpan={13} className="p-0">
                        <SubAssetsInlineTable parentAsset={a} />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })
          )}
        </TableBody>
        </Table>
        <div className="flex items-center justify-between border-t p-3 text-sm">
          <span className="text-muted-foreground">
            Page {filters.page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={(filters.page ?? 1) <= 1}
              onClick={() => setFilters({ ...filters, page: (filters.page ?? 1) - 1 })}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={(filters.page ?? 1) >= totalPages}
              onClick={() => setFilters({ ...filters, page: (filters.page ?? 1) + 1 })}
            >
              Next
            </Button>
          </div>
        </div>
      </Card>

      <QrModal asset={qrModal.asset} open={qrModal.open} onOpenChange={qrModal.onOpenChange} />

      <AllocateAssetDialog asset={toAllocate} open={!!toAllocate} onOpenChange={(o) => !o && setToAllocate(null)} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete asset?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <span className="font-mono">{toDelete?.asset_code}</span> — {toDelete?.name}.
              Linked documents, allocations, audit logs and history will also be removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!toDelete) return;
                try {
                  await deleteMut.mutateAsync(toDelete.id);
                  await queryClient.invalidateQueries({ queryKey: ["assets"] });
                  toast.success("Asset deleted");
                  setToDelete(null);
                } catch (e: unknown) {
                  toast.error((e as Error).message || "Delete failed");
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SubAssetsInlineTable({ parentAsset }: { parentAsset: AssetListRow }) {
  const navigate = useNavigate();
  const { data: subAssets, isLoading, isError } = useSubAssets(parentAsset.id);

  return (
    <div className="py-3 px-6 bg-slate-50/80 dark:bg-slate-900/40 border-l-4 border-l-emerald-500 my-2 mx-4 rounded-md shadow-inner space-y-2.5">
      <div className="flex items-center justify-between pb-2 border-b border-border/50">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-emerald-600" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Linked Sub-Assets of Parent:
          </span>
          <span className="text-xs font-medium text-foreground">
            {parentAsset.name} — <span className="font-mono">{parentAsset.asset_code || "Not Assigned"}</span>
          </span>
          <Badge variant="secondary" className="text-[11px] h-5 px-1.5 font-normal ml-1">
            {subAssets?.length ?? parentAsset.sub_assets_count ?? 0} {subAssets?.length === 1 ? "component" : "components"}
          </Badge>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            navigate({ to: "/assets/new", search: { parent_asset_id: parentAsset.id } });
          }}
        >
          <Plus className="mr-1 h-3 w-3" /> Add Sub Asset
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin text-emerald-600" />
          Loading linked sub assets…
        </div>
      ) : isError ? (
        <div className="text-xs text-destructive py-3">
          Failed to load sub assets.
        </div>
      ) : !subAssets || subAssets.length === 0 ? (
        <div className="text-xs text-muted-foreground py-4 text-center">
          No sub assets currently linked to this asset.
        </div>
      ) : (
        <div className="border rounded-md bg-background overflow-hidden shadow-sm">
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow className="h-8 hover:bg-transparent">
                <TableHead className="text-[11px] py-1 font-semibold">Asset Code</TableHead>
                <TableHead className="text-[11px] py-1 font-semibold">Sub Asset Name</TableHead>
                <TableHead className="text-[11px] py-1 font-semibold">Serial Number</TableHead>
                <TableHead className="text-[11px] py-1 font-semibold">Make / Model</TableHead>
                <TableHead className="text-[11px] py-1 font-semibold">Category</TableHead>
                <TableHead className="text-[11px] py-1 font-semibold">Location / Dept</TableHead>
                <TableHead className="text-[11px] py-1 font-semibold">Status</TableHead>
                <TableHead className="text-[11px] py-1 font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subAssets.map((sub) => (
                <TableRow
                  key={sub.id}
                  className="h-9 hover:bg-muted/40 cursor-pointer text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate({ to: "/assets/$assetId", params: { assetId: sub.id } });
                  }}
                >
                  <TableCell className="font-mono py-1.5">
                    {sub.asset_code ? (
                      <span className="font-semibold">{sub.asset_code}</span>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-amber-700 bg-amber-50 border-amber-300 font-normal">
                        Not Assigned
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-medium py-1.5 text-foreground flex items-center gap-1.5">
                    <span className="text-muted-foreground">↳</span>
                    {sub.name}
                  </TableCell>
                  <TableCell className="font-mono text-muted-foreground py-1.5">
                    {sub.serial_number || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground py-1.5">
                    {[sub.make, sub.model].filter(Boolean).join(" / ") || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground py-1.5">
                    {sub.category?.name || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground py-1.5">
                    {sub.location?.name ?? sub.department?.name ?? "—"}
                  </TableCell>
                  <TableCell className="py-1.5">
                    <StatusBadge value={sub.status ?? "—"} />
                  </TableCell>
                  <TableCell className="py-1.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-primary hover:text-primary/80"
                      onClick={() => navigate({ to: "/assets/$assetId", params: { assetId: sub.id } })}
                    >
                      <Eye className="mr-1 h-3 w-3" /> View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
